// import { StorageSerializers } from '@vueuse/core'
import { defineStore } from 'pinia'
import type { DateFormat, PersonalSettings } from '~/types/Account'
import { findAccountSettings, updateAccountSettings } from '~/utils/backend/accounts-api'

export const useSettingsStore = defineStore('Settings', () => {
  const defaultSettings: PersonalSettings = {
    username: '',
    email: null,
    use2FA: false,
    accountSettings: {
      defaultPcNumber: 1,
      language: {
        id: null,
        code: 'EN',
        label: 'English',
      },
      dateFormat: 'dd-LL-yyyy HH:mm',
    },
  }
  const securityStore = useSecurityStore()
  const { isLoggedIn } = storeToRefs(securityStore)
  const request = ref(Promise.resolve(defaultSettings))
  const controller = useAsyncState(() => findAccountSettings().then(r => processSettings(r.data)), defaultSettings, { immediate: false, shallow: true })
  const settings = controller.state
  const isLoading = controller.isLoading

  function processSettings(input: PersonalSettings) {
    input.accountSettings.dateFormat = applyClientDateFormat(input.accountSettings.dateFormat)
    return input
  }

  function applyClientDateFormat(input: DateFormat): DateFormat {
    return (input
      .replaceAll('DD', 'dd')
      .replaceAll('MM', 'LL')
      .replaceAll('YYYY', 'yyyy')
      .replaceAll('hh', 'HH')
    ) as DateFormat
  }

  function load() {
    if (!isLoading.value) {
      request.value = controller.execute()
    }
  }

  function unload() {
    request.value = Promise.resolve(defaultSettings)
    settings.value = { ...defaultSettings }
  }

  function saveAccountSettings(body: PersonalSettings): Promise<PersonalSettings> {
    body.accountSettings.dateFormat = applyServerDateFormat(body.accountSettings.dateFormat)

    const request: PersonalSettings = {
      ...body,
      accountSettings: {
        ...body.accountSettings,
        dateFormat: applyServerDateFormat(body.accountSettings.dateFormat),
      },
    }

    return updateAccountSettings(request).then((r) => {
      settings.value = {
        ...r.data,
        accountSettings: {
          ...r.data.accountSettings,
          dateFormat: applyClientDateFormat(r.data.accountSettings.dateFormat),
        },
      }
      return r.data
    })
  }

  function applyServerDateFormat(input: DateFormat): DateFormat {
    return (input
      .replaceAll('dd', 'DD')
      .replaceAll('LL', 'MM')
      .replaceAll('yyyy', 'YYYY')
      .replaceAll('HH', 'hh')
    ) as DateFormat
  }

  whenever(isLoggedIn, load, { immediate: true })
  whenever(() => !isLoggedIn.value, unload)

  return {
    settings,
    isLoading,
    request,
    load,
    unload,
    saveAccountSettings,
    applyClientDateFormat,
    applyServerDateFormat,
  }
})
